import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "react-oidc-context";
import {
  filterHotelsByFeature,
  getAllFeatureStatusValues,
  getAvailableFeatureStatuses,
  mapHomeListHotelForStats,
} from "../utils/featureHotelFilters";

const API_URL = process.env.REACT_APP_API_URL || "/api";

export const useStatsHotels = () => {
  const auth = useAuth();
  const token = auth.user?.access_token;

  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [featureFilter, setFeatureFilter] = useState("greenmiles");
  const [selectedFeatureStatuses, setSelectedFeatureStatuses] = useState([]);
  const isFirstLoadRef = useRef(true);

  const fetchHotels = useCallback(async () => {
    if (!token) {
      return;
    }

    try {
      if (isFirstLoadRef.current) {
        setLoading(true);
      }
      setError(null);

      const response = await fetch(`${API_URL}/home/list`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success === "OK" && Array.isArray(data.result)) {
        setHotels(data.result.map(mapHomeListHotelForStats));
      } else {
        throw new Error(data.message || "Erreur lors de la récupération des hôtels");
      }
    } catch (err) {
      console.error("Error fetching hotels for stats:", err);
      setError(err.message || "Erreur lors de la récupération des données");
      setHotels([]);
    } finally {
      setLoading(false);
      isFirstLoadRef.current = false;
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      return;
    }
    fetchHotels();
  }, [fetchHotels, token]);

  const hotelsForFeature = useMemo(() => {
    if (featureFilter === "all") {
      return hotels;
    }
    return hotels.filter((hotel) =>
      featureFilter === "lunishower" ? hotel.has_lunishower : hotel.has_greenmiles
    );
  }, [hotels, featureFilter]);

  const availableFeatureStatuses = useMemo(
    () => getAvailableFeatureStatuses(hotelsForFeature, featureFilter),
    [hotelsForFeature, featureFilter]
  );

  useEffect(() => {
    if (featureFilter === "all") {
      setSelectedFeatureStatuses([]);
      return;
    }
    setSelectedFeatureStatuses(getAllFeatureStatusValues(hotelsForFeature, featureFilter));
  }, [featureFilter, hotelsForFeature]);

  const filteredHotels = useMemo(() => {
    const list = filterHotelsByFeature(hotels, featureFilter, selectedFeatureStatuses);
    return [...list].sort((a, b) =>
      (a.hName || "").localeCompare(b.hName || "", undefined, { sensitivity: "base" })
    );
  }, [hotels, featureFilter, selectedFeatureStatuses]);

  const toggleFeatureStatus = useCallback((statusValue) => {
    setSelectedFeatureStatuses((prev) =>
      prev.includes(statusValue) ? prev.filter((s) => s !== statusValue) : [...prev, statusValue]
    );
  }, []);

  const clearFeatureStatusFilters = useCallback(() => {
    setSelectedFeatureStatuses([]);
  }, []);

  const selectAllFeatureStatuses = useCallback(() => {
    setSelectedFeatureStatuses(getAllFeatureStatusValues(hotelsForFeature, featureFilter));
  }, [hotelsForFeature, featureFilter]);

  return {
    hotels: filteredHotels,
    allHotels: hotels,
    loading,
    error,
    featureFilter,
    setFeatureFilter,
    selectedFeatureStatuses,
    availableFeatureStatuses,
    toggleFeatureStatus,
    clearFeatureStatusFilters,
    selectAllFeatureStatuses,
    filteredCount: filteredHotels.length,
    totalCount: hotels.length,
    refetch: fetchHotels,
  };
};
